"use client";

import { StoreImage } from "@/components/StoreImage";
import {
  type PoQuotaContext,
  type ProductVariation,
  type VariantGroup,
  type VariantShift,
  describeVariantShift,
  hasEligibleVariationForOption,
  isVariationEligible,
  isVariantOptionSelectable,
} from "@/lib/products";
import { isOptionBlocked, isOptionFullyBlocked, type BlockedComboKeys } from "@/lib/lazyVariants";
import { optionPriceExtra, priceExtraOptionSuffix } from "@/lib/priceExtraLabel";

// ─── VariantGroupSelector ────────────────────────────────────────────────────
//
// Render variasi dipisah per grup (misal: Warna / Ukuran) sebagai tombol.
// Dipakai bersama oleh halaman produk (ProductDetail) DAN saat reseller
// mengedit pesanan OPEN PO (OrdersClient → EditModal), supaya pemilihan varian
// selalu berupa tombol yang mudah dipahami — bukan dropdown.
//
// Logika: untuk setiap grup, filter variasi yang memiliki option_id dari grup
// tersebut, lalu tampilkan sebagai baris tombol dengan label nama grup di atas.
//
// Jika produk hanya punya 1 grup, label grup disembunyikan (tidak perlu).

/** Tanpa daftar kombinasi mati, tak ada satu pun opsi yang dikunci karenanya. */
const NO_BLOCKED_COMBOS: ReadonlySet<string> = new Set<string>();

export function VariantGroupSelector({
  groups,
  variations,
  selected,
  onSelect,
  stockTracked,
  quota,
  lazy = false,
  selectedOptionIds,
  onSelectOption,
  onOptionPicked,
  blockedCombos,
  onVariantShift,
  showPriceExtra = true,
}: {
  groups: VariantGroup[];
  variations: ProductVariation[];
  /**
   * Label harga tambahan per opsi, mis. "XXL (+Rp 20.000)" (mig 7900, R1) —
   * hanya bila angkanya pasti (`optionPriceExtra`). `false` saat harga produk
   * disembunyikan. Mode malas tak berlabel (daftar variannya tak dikirim).
   */
  showPriceExtra?: boolean;
  selected: ProductVariation | undefined;
  onSelect: (v: ProductVariation) => void;
  /** Produk ready stock → grey-out option yang stoknya habis. */
  stockTracked: boolean;
  /**
   * Produk PO ber-toggle "Stok PO Terbatas" (mig 3970) → grey-out option yang
   * seluruh kombinasinya sudah habis jatah. Absen = PO tak terbatas, yaitu
   * perilaku lama: opsi tak pernah dikunci.
   */
  quota?: PoQuotaContext;
  /**
   * MODE MALAS (`lazyVariants.ts`): `variations` kosong karena kombinasinya
   * terlalu banyak untuk dikirim. Pemilihan jadi berbasis OPTION ID, dan
   * kombinasinya diresolusi pemanggil.
   *
   * Hanya dipakai produk PREORDER — pada ready stock, grey-out opsi butuh stok
   * seluruh kombinasi sehingga daftarnya wajib lengkap.
   */
  lazy?: boolean;
  selectedOptionIds?: string[];
  onSelectOption?: (optionId: string) => void;
  /**
   * Opsi yang BARU SAJA diketuk pembeli — dipanggil di KEDUA mode, sebelum
   * varian diresolusi. Halaman produk memakainya untuk melompatkan slider foto
   * ke foto opsi tersebut (`buildProductGallery`).
   *
   * Sengaja terpisah dari `onSelect`/`onSelectOption`: keduanya memulangkan
   * HASIL pilihan (varian / kombinasi option id lengkap), sedangkan yang
   * dibutuhkan foto adalah opsi mana yang diketuk. Pada produk multi-grup,
   * mengklik "Merah" memulangkan varian ber-optionIds `[merah, L]` — dari situ
   * mustahil tahu foto mana yang harus tampil, karena "L" pun bisa berfoto.
   *
   * Ikut terpanggil saat opsi yang SUDAH aktif diketuk lagi: itu justru cara
   * pembeli kembali ke foto opsi setelah menggeser slider.
   */
  onOptionPicked?: (optionId: string) => void;
  /**
   * Kombinasi yang DIMATIKAN admin (`product_variants.is_active = false`),
   * sudah berbentuk himpunan kunci (`buildBlockedComboSet`).
   *
   * Opsi yang mati di SELURUH kombinasi diperlakukan persis seperti stok habis
   * (abu + dicoret, tak bisa diketuk). Opsi yang cuma mati pada kombinasi yang
   * SEDANG dipilih hanya diredupkan — menguncinya menjebak pembeli pada produk
   * yang butuh dua grup berubah sekaligus (`isOptionBlocked` menceritakannya).
   *
   * Wajib dipegang terpisah karena baris nonaktif tak pernah terbaca pembeli
   * (RLS) — rinciannya di `lazyVariants.ts`. Kosong = tak ada yang dikunci.
   */
  blockedCombos?: BlockedComboKeys;
  /**
   * Grup LAIN yang ikut berganti pilihan karena opsi ini diketuk — kombinasi
   * persis yang diminta tidak tersedia, jadi selektor mendarat di varian
   * terdekat yang masih bisa dipesan (lihat `describeVariantShift`). Dipanggil
   * di SETIAP klik mode non-malas, dengan daftar kosong saat tak ada yang
   * bergeser, supaya pemanggil bisa membersihkan catatannya.
   *
   * Opsional: layar admin yang memakai selektor ini (Kelola PO, keranjang
   * manual) tidak menampilkan catatan apa pun dan tak perlu berubah.
   */
  onVariantShift?: (shifts: VariantShift[]) => void;
}) {
  // Bangun map: optionId → variation yang mengandungnya
  // Satu variasi bisa punya banyak optionId (kombinasi multi-grup)
  const optionToVariations = new Map<string, ProductVariation[]>();
  for (const v of variations) {
    for (const oid of v.optionIds ?? []) {
      const existing = optionToVariations.get(oid) ?? [];
      existing.push(v);
      optionToVariations.set(oid, existing);
    }
  }

  const showGroupLabel = groups.length > 1;

  return (
    <div className="space-y-4">
      {groups.map((group) => {
        // Option yang terpilih dalam grup ini. Mode malas memakai daftar option
        // id yang dipegang pemanggil (varian belum tentu sudah ter-resolusi);
        // mode biasa menurunkannya dari varian terpilih.
        const selectedOptionId = lazy
          ? group.options.find((opt) => selectedOptionIds?.includes(opt.id))?.id
          : group.options.find((opt) => selected?.optionIds?.includes(opt.id))
              ?.id;

        return (
          <div key={group.id}>
            {showGroupLabel && (
              <p className="mb-2 text-[11px] font-extrabold uppercase tracking-wide text-muted">
                {group.name}
                {selectedOptionId && (
                  <span className="ml-1.5 font-bold normal-case text-ink">
                    :{" "}
                    {group.options.find((o) => o.id === selectedOptionId)?.name}
                  </span>
                )}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {group.options.map((opt) => {
                const candidateVariations = optionToVariations.get(opt.id) ?? [];
                const isActive = lazy
                  ? (selectedOptionIds?.includes(opt.id) ?? false)
                  : (selected?.optionIds?.includes(opt.id) ?? false);
                // Sumber tunggal keputusan enable/disable (products.ts):
                //  - preorder (stockTracked=false) → SELALU bisa dipilih walau stok 0
                //    (preorder mengikuti pesanan; stok 0 normal),
                //  - ready stock → bisa dipilih bila ADA varian in-stock yang memuat
                //    opsi ini (availability agregat lintas kombinasi, ala Shopee).
                // Mode malas hanya untuk preorder, dan preorder TIDAK PERNAH
                // mengunci opsi karena stok (stok 0 itu normal — barangnya
                // memang belum ada). Jadi keputusannya sama dengan yang
                // diambil `isVariantOptionSelectable` untuk stockTracked=false,
                // tanpa perlu memegang daftar kombinasi.
                // ⚠️ Catatan lama di sini menyatakan "mode malas TIDAK bisa
                // berkuota". SALAH, dan dibuktikan salah di PROD: natasyashop
                // "Abinaya famset by medina" berjalan mode malas (440
                // kombinasi) SEKALIGUS `po_limited` — 17 produk sejenis ada
                // hari ini. Yang menutup kombinasi tertutup PO berkuota di
                // mode malas kini `ProductDetail` (kombinasi tak bermateri =
                // pasti ditolak RPC), bukan asumsi bahwa keduanya tak pernah
                // bertemu.
                //
                // KOMBINASI YANG DIMATIKAN ADMIN dinilai lebih dulu dan
                // berlaku di KEDUA mode: baris nonaktif tak pernah terbaca
                // pembeli (RLS), jadi ia mustahil disimpulkan dari
                // `variations` — lihat `lazyVariants.ts`.
                const currentSelection = lazy
                  ? (selectedOptionIds ?? [])
                  : (selected?.optionIds ?? []);
                const blockedSet = blockedCombos ?? NO_BLOCKED_COMBOS;
                // DUA pertanyaan berbeda tentang kombinasi yang dimatikan
                // admin, dan MENCAMPURNYA pernah membuat varian yang masih
                // dijual mustahil dicapai (natasyashop "Navara famset",
                // 2026-09-14 — rinciannya di `isOptionBlocked`):
                //   · MATI TOTAL  → opsi ini tak hidup di kombinasi mana pun.
                //                   Hanya ini yang boleh MENGUNCI tombol; tak
                //                   ada yang bisa dijebak karena memang tak
                //                   ada tujuan.
                //   · MATI DI SINI → hidup, tapi tidak bersama pilihan grup
                //                   lain yang sekarang. Tombolnya REDUP dan
                //                   tetap bisa diketuk; pemanggil menggeser
                //                   grup lain ke kombinasi hidup terdekat.
                const comboBlocked = isOptionBlocked(
                  groups,
                  currentSelection,
                  opt.id,
                  blockedSet
                );
                const fullyBlocked =
                  comboBlocked && isOptionFullyBlocked(groups, opt.id, blockedSet);
                const disabled =
                  fullyBlocked ||
                  (lazy
                    ? false
                    : !isVariantOptionSelectable(
                        stockTracked,
                        variations,
                        opt.id,
                        quota
                      ));

                // Pilihan grup LAIN yang sedang aktif — dasar dua keputusan di
                // bawah (penanda redup, lalu pemilihan varian saat diketuk).
                const otherSelectedOptionIds = currentSelection.filter(
                  (oid) => !group.options.some((o) => o.id === oid)
                );

                // TIDAK COCOK DENGAN PILIHAN SEKARANG — beda dari `disabled`,
                // dan bedanya penting:
                //   · `disabled` = opsi ini mati di SELURUH kombinasi (habis
                //     total / dimatikan admin) → tak bisa diketuk, dicoret;
                //   · `mismatched` = opsi ini HIDUP, tapi tidak bersama pilihan
                //     grup lain yang sekarang terlihat → tetap bisa diketuk,
                //     hanya ditampilkan redup, karena mengetuknya akan
                //     menggeser grup lain (dan pergeseran itu dilaporkan
                //     lewat `onVariantShift`).
                // Menguncinya alih-alih meredupkan akan MENJEBAK pembeli:
                // Biru yang habis pada ukuran M jadi mustahil dipilih walau
                // Biru/L tersedia.
                //
                // Mode malas tak ikut: daftar variannya memang tidak dikirim,
                // dan yang benar-benar mati di sana sudah dicoret lewat
                // `blockedCombos`.
                const mismatched =
                  !disabled &&
                  (comboBlocked ||
                    (!lazy &&
                      otherSelectedOptionIds.length > 0 &&
                      !hasEligibleVariationForOption(
                        stockTracked,
                        variations,
                        opt.id,
                        otherSelectedOptionIds,
                        quota
                      )));

                const handleClick = () => {
                  if (disabled) return;
                  onOptionPicked?.(opt.id);
                  if (lazy) {
                    onSelectOption?.(opt.id);
                    return;
                  }
                  const allRequired = [opt.id, ...otherSelectedOptionIds];

                  // Prioritas pemilihan varian saat klik:
                  //  1. kombinasi sama persis + masih in-stock (pertahankan pilihan),
                  //  2. varian in-stock mana pun yang memuat option ini,
                  //  3. kombinasi sama persis (fallback non-stock / semua habis),
                  //  4. kandidat pertama.
                  // Varian "layak" = in-stock (ready stock) ATAU masih ber-jatah
                  // (PO berkuota). Tanpa ini, mengklik Warna yang ukurannya
                  // habis akan memilih kombinasi mati padahal ada ukuran lain
                  // yang masih terbuka.
                  const isEligible = (v: ProductVariation) =>
                    isVariationEligible(stockTracked, v, quota);
                  const filterEligible = stockTracked || Boolean(quota?.limited);

                  const best =
                    (filterEligible
                      ? candidateVariations.find(
                          (v) =>
                            allRequired.every((oid) => v.optionIds?.includes(oid)) &&
                            isEligible(v)
                        )
                      : undefined) ??
                    (filterEligible
                      ? candidateVariations.find(isEligible)
                      : undefined) ??
                    candidateVariations.find((v) =>
                      allRequired.every((oid) => v.optionIds?.includes(oid))
                    ) ??
                    candidateVariations[0];

                  onSelect(best);

                  // Kombinasi persis yang diminta tak tersedia → varian yang
                  // terpilih membawa opsi grup LAIN. Katakan apa yang bergeser;
                  // diam di sini berarti pembeli bisa memesan warna/ukuran yang
                  // tidak pernah ia pilih. Selalu dipanggil (termasuk dengan
                  // daftar kosong) supaya catatan lama ikut hilang begitu
                  // pembeli mengetuk opsi yang cocok.
                  onVariantShift?.(
                    describeVariantShift(
                      groups,
                      selected?.optionIds ?? [],
                      best?.optionIds ?? [],
                      group.id
                    )
                  );
                };

                // Harga tambahan PASTI untuk opsi ini bersama pilihan grup lain.
                const extraSuffix =
                  showPriceExtra && !lazy
                    ? priceExtraOptionSuffix(
                        optionPriceExtra(variations, opt.id, otherSelectedOptionIds)
                      )
                    : "";

                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={disabled}
                    onClick={handleClick}
                    className={`min-h-11 min-w-[3.5rem] rounded-xl border px-3.5 py-1 text-sm font-bold transition
                      ${isActive
                        ? "border-shopee bg-shopee text-white shadow-soft"
                        : disabled
                          ? "cursor-not-allowed border-zinc-200 bg-zinc-200/70 text-zinc-400 line-through"
                          : mismatched
                            ? "border-dashed border-zinc-300 bg-white text-ink/45 active:bg-shopeeSoft/60"
                            : "border-neutral-100 bg-white text-ink active:bg-shopeeSoft/60"
                      }`}
                    title={
                      mismatched
                        ? "Tidak tersedia untuk kombinasi yang sedang dipilih — memilihnya akan menyesuaikan pilihan grup lain."
                        : undefined
                    }
                  >
                    {opt.imageUrl ? (
                      <span className="flex items-center gap-1.5">
                        <StoreImage
                          src={opt.imageUrl}
                          alt={opt.name}
                          width={20}
                          height={20}
                          className="h-5 w-5 rounded-full object-cover"
                        />
                        {opt.name}
                        {extraSuffix ? (
                          <span className="text-[10px] font-semibold opacity-80">{extraSuffix.trim()}</span>
                        ) : null}
                      </span>
                    ) : extraSuffix ? (
                      <span>
                        {opt.name}{" "}
                        <span className="text-[10px] font-semibold opacity-80">{extraSuffix.trim()}</span>
                      </span>
                    ) : (
                      opt.name
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
